import { useEffect } from 'react';
import MeetingDto from '../../../types/MeetingDto';
import type { HasResult } from '../features/ReviewCycleOverview/services/anomalyDetector';

export enum EventType {
    MeetingCreated,
    MeetingBulkUpdate,
    Shake,
    UnauthorizedApiResponse,
    ReviewCycleOutlierPillClick,
}

interface ValueTypeByEvent {
    [EventType.MeetingCreated]: MeetingDto;
    [EventType.MeetingBulkUpdate]: MeetingDto[];
    [EventType.Shake]: string;
    [EventType.UnauthorizedApiResponse]: any;
    [EventType.ReviewCycleOutlierPillClick]: HasResult;
}

const subscribers: { type: EventType; callback: (value: any) => void }[] = [];

export function subscribe(type: EventType, callback: (value: any) => void): () => void {
    const listener = { type, callback };
    subscribers.push(listener);

    return () => subscribers.splice(subscribers.indexOf(listener), 1);
}

function publish(type: EventType, value: any): void {
    subscribers.filter(listener => listener.type === type).forEach(listener => listener.callback(value));
}

export default function useEventBus<T extends EventType>(type: T, callback: (value: ValueTypeByEvent[T]) => void) {
    useEffect(() => subscribe(type, callback), [type, callback]);
}

// This is not a React Hook, it can be used outside a React component.
export function createEventBusPublisher<T extends EventType>(type: T) {
    return (value: ValueTypeByEvent[T]) => publish(type, value);
}
